"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";

import { useState } from "react";

import { Check, Loader2, Minus, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/frontend/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/frontend/components/ui/dialog";
import { useCreateExtraHoursCheckout } from "@/frontend/hooks/subscription";
import { getErrorMessage } from "@/frontend/lib/error-utils";
import { EXTRA_HOURS } from "@/frontend/lib/plans";
import { useSidebarStore } from "@/frontend/lib/stores";

export function GetExtraDialog() {
  const router = useRouter();
  const { isGetExtraDialogOpen, setIsGetExtraDialogOpen } = useSidebarStore();

  const [quantity, setQuantity] = useState(1);

  const createExtraHoursCheckoutSessionMutation = useCreateExtraHoursCheckout();

  const increaseQuantity = () => {
    setQuantity((prev) => prev + 1);
  };

  const decreaseQuantity = () => {
    setQuantity((prev) => (prev > 1 ? prev - 1 : 1));
  };

  const handleCheckout = () => {
    createExtraHoursCheckoutSessionMutation.mutate(
      { quantity },
      {
        onSuccess: (url) => {
          router.push(url);
        },
        onError: (error) => {
          toast.error(
            getErrorMessage(error, "Failed to create checkout session"),
          );
        },
      },
    );
  };

  // Calculate total price
  const unitPrice = EXTRA_HOURS[30].price;
  const totalPrice = unitPrice * quantity;
  const totalHours = (EXTRA_HOURS[30].extraMinutes / 60) * quantity;

  return (
    <Dialog open={isGetExtraDialogOpen} onOpenChange={setIsGetExtraDialogOpen}>
      <DialogContent
        fullscreen
        className="flex flex-col items-center rounded-none not-standalone:overflow-y-auto sm:max-w-full md:gap-12 standalone:justify-center"
        style={{
          scrollbarWidth: "none",
        }}
      >
        <DialogHeader className="gap-2 not-standalone:mt-16 md:gap-4">
          <DialogTitle className="text-center text-xl font-semibold tracking-tight md:text-4xl">
            Get extra agent hours
          </DialogTitle>
          <DialogDescription className="text-center text-sm text-muted md:text-base">
            Extend your monthly Scout usage.{" "}
            <Link
              href="/faq"
              className="text-primary hover:underline"
              onClick={() => setIsGetExtraDialogOpen(false)}
            >
              Learn more.
            </Link>
          </DialogDescription>
        </DialogHeader>

        <div className="flex w-full max-w-lg flex-col">
          <div className="flex h-full w-full flex-col gap-4 rounded-3xl border bg-input p-4 text-foreground">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center justify-center rounded-sm bg-white p-1 shadow-xs">
                <div className="rounded-xs bg-primary/10 p-1.5">
                  <Sparkles size={20} className="text-primary" />
                </div>
              </div>
            </div>

            <div className="flex flex-col text-left">
              <h3 className="text-lg font-semibold">Extra agent hours</h3>
              <div className="h-5 text-sm text-muted-foreground">
                One-time purchase
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <span className="text-3xl font-semibold">${unitPrice}</span>
                <span className="ml-1 text-sm font-medium text-muted-foreground">
                  per 30 hours
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={decreaseQuantity}
                  disabled={quantity <= 1}
                  className="h-8 w-8"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <div className="min-w-[80px] text-center">
                  <div className="text-lg font-medium">{totalHours} hours</div>
                </div>
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={increaseQuantity}
                  className="h-8 w-8"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <ul className="h-full space-y-4 rounded-xl bg-white p-4 text-start">
              <li className="flex items-start gap-2">
                <Check
                  className="mt-0.5 flex-shrink-0 text-muted-foreground"
                  size={16}
                />
                <div className="text-sm text-foreground/90">
                  Get {totalHours} extra agent hours immediately
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Check
                  className="mt-0.5 flex-shrink-0 text-muted-foreground"
                  size={16}
                />
                <div className="text-sm text-foreground/90">
                  Extra hours will only be used after monthly usage runs out
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Check
                  className="mt-0.5 flex-shrink-0 text-muted-foreground"
                  size={16}
                />
                <div className="text-sm text-foreground/90">
                  Can only be used while subscribed to paid plan
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Check
                  className="mt-0.5 flex-shrink-0 text-muted-foreground"
                  size={16}
                />
                <div className="text-sm text-foreground/90">
                  Extra hours never expire
                </div>
              </li>
            </ul>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Total payment
              </span>
              <span className="text-lg font-semibold">${totalPrice}</span>
            </div>

            <Button
              variant="default"
              className="w-full rounded-xl bg-foreground hover:bg-foreground/90"
              onClick={handleCheckout}
              disabled={createExtraHoursCheckoutSessionMutation.isPending}
            >
              {createExtraHoursCheckoutSessionMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Purchase extra hours"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
